import { useSnackbar } from 'notistack';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import ApiClient from '../config/ApiClient';
import { LessonPlanModel, LessonPlanSessionModel, SessionSlideModel } from '../object-actions/types/types';

interface LessonPlanEditorContextType {
    // State
    isEditMode: boolean;
    lessonPlan: LessonPlanModel | null;
    loading: boolean;
    hasChanges: boolean;

    // Methods
    updateSession: (sessionId: number, updates: Partial<LessonPlanSessionModel>) => void;
    updateSlide: (slideId: number, updates: Partial<SessionSlideModel>) => void;
    saveAll: () => Promise<void>;
    resetChanges: () => void;
    toggleEditMode: () => void;
    setLessonPlan: (plan: LessonPlanModel | null) => void;
    getSessionWithEdits: (session: LessonPlanSessionModel) => LessonPlanSessionModel;
    getSlideWithEdits: (slide: SessionSlideModel) => SessionSlideModel;
}

const LessonPlanEditorContext = createContext<LessonPlanEditorContextType | null>(null);

export function useLessonPlanEditor() {
    const context = useContext(LessonPlanEditorContext);
    if (!context) {
        throw new Error('useLessonPlanEditor must be used within LessonPlanEditorProvider');
    }
    return context;
}

interface Props {
    children: ReactNode;
    initialLessonPlan?: LessonPlanModel | null;
}

export function LessonPlanEditorProvider({ children, initialLessonPlan = null }: Props) {
    const { enqueueSnackbar } = useSnackbar();
    const [lessonPlan, setLessonPlanState] = useState<LessonPlanModel | null>(initialLessonPlan);
    const [editedSessions, setEditedSessions] = useState<Map<number, Partial<LessonPlanSessionModel>>>(new Map());
    const [editedSlides, setEditedSlides] = useState<Map<number, Partial<SessionSlideModel>>>(new Map());
    const [loading, setLoading] = useState(false);

    // Determine edit mode from hash (only coaches can edit)
    const [isEditMode, setIsEditMode] = useState(() => {
        if (typeof window !== 'undefined') {
            return window.location.hash === '#editing=true';
        }
        return false;
    });

    const isCoach = lessonPlan?.my_roles?.includes('coach') ?? false;
    const effectiveIsEditMode = isEditMode && isCoach;

    // Update edit mode when hash changes (only applies when user is coach)
    useEffect(() => {
        const handleHashChange = () => {
            setIsEditMode(window.location.hash === '#editing=true');
        };

        window.addEventListener('hashchange', handleHashChange);
        return () => window.removeEventListener('hashchange', handleHashChange);
    }, []);

    // Update lesson plan when initialLessonPlan changes
    useEffect(() => {
        if (initialLessonPlan) {
            setLessonPlanState(initialLessonPlan);
            // Reset edits when lesson plan changes
            setEditedSessions(new Map());
            setEditedSlides(new Map());
        }
    }, [initialLessonPlan?.id]);

    // Calculate hasChanges
    const hasChanges = useMemo(() => {
        return editedSessions.size > 0 || editedSlides.size > 0;
    }, [editedSessions.size, editedSlides.size]);

    // Memoized updateSession method
    const updateSession = useCallback((sessionId: number, updates: Partial<LessonPlanSessionModel>) => {
        setEditedSessions(prev => {
            const newMap = new Map(prev);
            const existing = newMap.get(sessionId) || {};
            newMap.set(sessionId, { ...existing, ...updates });
            return newMap;
        });
    }, []);

    // Memoized updateSlide method
    const updateSlide = useCallback((slideId: number, updates: Partial<SessionSlideModel>) => {
        setEditedSlides(prev => {
            const newMap = new Map(prev);
            const existing = newMap.get(slideId) || {};
            newMap.set(slideId, { ...existing, ...updates });
            return newMap;
        });
    }, []);

    // Save all changes
    const saveAll = useCallback(async () => {
        if (!lessonPlan || (!hasChanges && editedSessions.size === 0 && editedSlides.size === 0)) {
            return;
        }

        setLoading(true);
        try {
            const errors: string[] = [];

            // Save session updates
            for (const [sessionId, updates] of editedSessions.entries()) {
                try {
                    const response = await ApiClient.patch(
                        `/api/lesson-plan-sessions/${sessionId}/`,
                        updates
                    );
                    if (!response.success) {
                        errors.push(`Failed to update session ${sessionId}: ${response.error || 'Unknown error'}`);
                    }
                } catch (error: any) {
                    errors.push(`Failed to update session ${sessionId}: ${error.message || 'Unknown error'}`);
                }
            }

            // Save slide updates
            for (const [slideId, updates] of editedSlides.entries()) {
                try {
                    const response = await ApiClient.patch(
                        `/api/session-slides/${slideId}/`,
                        updates
                    );
                    if (!response.success) {
                        errors.push(`Failed to update slide ${slideId}: ${response.error || 'Unknown error'}`);
                    }
                } catch (error: any) {
                    errors.push(`Failed to update slide ${slideId}: ${error.message || 'Unknown error'}`);
                }
            }

            if (errors.length > 0) {
                enqueueSnackbar(`Some updates failed: ${errors.join('; ')}`, { variant: 'error' });
            } else if (editedSessions.size > 0 || editedSlides.size > 0) {
                enqueueSnackbar('Changes saved successfully!', { variant: 'success' });
                // Clear edits after successful save
                setEditedSessions(new Map());
                setEditedSlides(new Map());
                // Refetch lesson plan to get updated data
                if (lessonPlan?.id) {
                    const response = await ApiClient.get<LessonPlanModel>(
                        `/api/lesson-plan/${lessonPlan.id}?include_sessions=true`
                    );
                    if (response.success && response.data) {
                        setLessonPlanState(response.data);
                    }
                }
            }
        } catch (error: any) {
            enqueueSnackbar(`Failed to save changes: ${error.message || 'Unknown error'}`, { variant: 'error' });
        } finally {
            setLoading(false);
        }
    }, [lessonPlan, editedSessions, editedSlides, hasChanges, enqueueSnackbar]);

    // Reset all changes
    const resetChanges = useCallback(() => {
        setEditedSessions(new Map());
        setEditedSlides(new Map());
    }, []);

    // Toggle edit mode via hash
    const toggleEditMode = useCallback(() => {
        if (isEditMode) {
            window.location.hash = '';
        } else {
            window.location.hash = '#editing=true';
        }
    }, [isEditMode]);

    // Set lesson plan
    const setLessonPlan = useCallback((plan: LessonPlanModel | null) => {
        setLessonPlanState(plan);
        // Reset edits when lesson plan is set
        setEditedSessions(new Map());
        setEditedSlides(new Map());
    }, []);

    // Get merged session data (original + edits). Slide-level edits (e.g. started_at from
    // update_slide_progress) are merged into session.slides so areAllSlidesTrackedForSession
    // and Mark Complete visibility update without refetch.
    const getSessionWithEdits = useCallback((session: LessonPlanSessionModel): LessonPlanSessionModel => {
        const sessionEdits = editedSessions.get(session.id);
        const base = sessionEdits ? { ...session, ...sessionEdits } : session;
        if (!session.slides?.length) return base;
        const slidesWithEdits = session.slides.map((s) => {
            if (!s) return s;
            const slideEdits = editedSlides.get(s.id);
            return slideEdits ? { ...s, ...slideEdits } : s;
        });
        return { ...base, slides: slidesWithEdits };
    }, [editedSessions, editedSlides]);

    // Get merged slide data (original + edits)
    const getSlideWithEdits = useCallback((slide: SessionSlideModel): SessionSlideModel => {
        const edits = editedSlides.get(slide.id);
        return edits ? { ...slide, ...edits } : slide;
    }, [editedSlides]);

    const value: LessonPlanEditorContextType = useMemo(() => ({
        isEditMode: effectiveIsEditMode,
        lessonPlan,
        loading,
        hasChanges,
        updateSession,
        updateSlide,
        saveAll,
        resetChanges,
        toggleEditMode,
        setLessonPlan,
        getSessionWithEdits,
        getSlideWithEdits
    }), [effectiveIsEditMode, lessonPlan, loading, hasChanges, updateSession, updateSlide, saveAll, resetChanges, toggleEditMode, setLessonPlan, getSessionWithEdits, getSlideWithEdits]);

    return (
        <LessonPlanEditorContext.Provider value={value}>
            {children}
        </LessonPlanEditorContext.Provider>
    );
}
